import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as z from 'zod';

import { FormGroup } from '../shared/components/FormGroup';
import { Loader } from '../shared/components/Loader';
import { PasswordHints } from '../shared/components/PasswordHints';
import { PasswordInput } from '../shared/components/PasswordInput';
import { usePatientUser } from '../shared/hooks/usePatientUser';

import { CreatePasswordErrorFallback } from './CreatePasswordErrorFallback';
import styles from './CreatePasswordForm.module.scss';
import parentStyles from './CreatePasswordPage.module.scss';

const schema = z.object({
  email: z.string().min(1),
  password: z
    .string()
    .min(1)
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+:])(?=.{8,})/),
});

type FormValues = z.infer<typeof schema>;

export function CreatePasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const username = searchParams.get('username');
  const confirmationCode = searchParams.get('code');

  const { user, setPassword } = usePatientUser(username);
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async ({ password }: any) => {
      setIsProcessing(true);
      await setPassword(password, confirmationCode).catch(() => {
        //FIXME: show a toast message with the error
      });
      setIsProcessing(false);

      navigate('success');
    },
    [navigate, setPassword, confirmationCode]
  );

  if (!username) {
    return <CreatePasswordErrorFallback error={new Error('empty username')} />;
  }

  return (
    <div className={styles.container}>
      <h1 className={parentStyles.title}>
        <FormattedMessage
          id="create-password-page.title"
          defaultMessage="Finish registering your account"
        />
      </h1>
      <form className={styles.form} noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGroup error={errors['email']}>
          <label htmlFor="userEmail">
            <FormattedMessage
              id="create-password-page.email-input-label"
              defaultMessage="Your email address"
            />
            <Link
              to={{ pathname: 'change-email', search: searchParams.toString() }}
              className={styles.changeEmailLink}
            >
              <FormattedMessage
                id="create-password-page.change-email-link"
                defaultMessage="Change email"
              />
            </Link>
          </label>
          <input
            id="userEmail"
            type="email"
            {...register('email')}
            defaultValue={user?.email}
            readOnly
          />
        </FormGroup>

        <FormGroup
          error={errors['password']}
          errorMessages={{
            too_small: (
              <FormattedMessage
                id="create-password-page.password-input-required"
                defaultMessage="Please create a password"
              />
            ),
            invalid_format: (
              <>
                <strong>
                  <FormattedMessage
                    id="create-password-page.password-input-error"
                    defaultMessage="Please make sure your password contains:"
                  />
                </strong>
                <PasswordHints />
              </>
            ),
          }}
          help={
            <>
              <FormattedMessage
                id="create-password-page.password-input-help"
                defaultMessage="Password must contain:"
              />
              <PasswordHints />
            </>
          }
        >
          <label htmlFor="userPassword">
            <FormattedMessage
              id="create-password-page.password-input-label"
              defaultMessage="Create password"
            />
          </label>
          <PasswordInput id="userPassword" {...register('password')} />
        </FormGroup>

        <div className={styles.actions}>
          <button type="submit" disabled={isProcessing}>
            <FormattedMessage id="create-password-page.form-submit-btn" defaultMessage="Continue" />
          </button>
        </div>
      </form>
      {isProcessing && <Loader fullscreen />}
    </div>
  );
}
